// A whole device compared, in one place (#369): what Zenith intends for it
// beside what it reports, for the device and — on a switch — every port at
// once, where the per-port detail (`PortDetail.tsx`, #294) answers one port at
// a time.
//
// **The device.** The one intent-versus-actual pair §11.2 defines for a
// device is its configuration generation: the one the controller holds
// against the one the device reports running. The two classes reach it by
// different reads and do not say the same thing about it:
//
// - an access point's pair comes from `/sites/{id}/convergence`, the read the
//   page already makes, with the state word and its `detail` (ADR-0022
//   decision 3);
// - a switch's comes from its own single-device read (#421), as two numbers
//   and whether they differ. Since ADR-0074 a switch does have a state word —
//   judged against what its own controller holds, by `/sites/{id}/convergence`,
//   and shown in the head and the list's Running column. This section was left
//   as it was by #534 and still shows the pair and not the word: the word is
//   one read away, and a second rendering of it here would be a second place
//   for the two to disagree. **It says what it compared** (#573, ADR-0078
//   decision 1): "differs", or "the switch reports the generation its
//   controller holds". It used to say "the same", and said it of a switch
//   running two hand changes, because a hand change moves no generation.
//   `Held` and `Running` are the right words here and are ADR-0074's: this
//   Running is the switch's own report.
//
// Firmware, address and uptime are facts with no stored intent to compare
// against; they are the identity panel's, and this section says so rather
// than drawing them twice.
//
// **The ports.** One row per port of the switch's document, from the plan's
// `changes` — which since #369 carries every port the document declares, not
// only the ones intent names. Each field shows what the cell **holds** for the
// switch, and where the apply would change it, what it would send instead.
// Held and not running (#573, ADR-0078 decision 1): the plan reads the cell's
// copy, so a port changed at the switch's console reads here as it did before.
// **The marks are the plan's `differs` and nothing else**: this page compares
// no two values itself (#294). A port the intent does not name has no
// `planned` side; it reads "no intent; sent as the cell holds it", which is
// the contract's meaning.
// `/v1` exposes no other read of a switch's document, so without a plan on
// screen the table says so and does not guess.
import type { components } from "../../api/schema";
import { plural } from "../../dashboard/format";
import { Card, Info, Loading, Pill } from "../../ui";
import type { Device } from "./commands";
import { PortName } from "./DeviceFace";
import { convergenceWord, type useConvergence } from "./DevicesTable";
import { ROWS, word } from "./PortDetail";

type Plan = components["schemas"]["SwitchPortPlan"];
type Change = components["schemas"]["SwitchPortChange"];

export default function Compared({ device, read, readStatus, convergence, plan, planned = false, selected, onSelect }: {
  device: Device;
  /** The device's own read (`GET /devices/{serial}`), which carries a switch's generation pair. */
  read?: Device;
  readStatus: string;
  /** The site's convergence read, the page's own; an access point's pair and state are in it. */
  convergence?: ReturnType<typeof useConvergence>;
  /** The switch's plan on screen, when there is one: the ports' held and planned sides. */
  plan?: Plan;
  /** Whether a plan can be made under this, beside the editor — Design ›
   * Wired's Ports tab — or not, on a device's page in Operate (#875). */
  planned?: boolean;
  selected?: string;
  onSelect?(port: string): void;
}) {
  const isSwitch = device.category === "switch";
  return (
    <Card className="panel" title="Compared"
      head={<Info label="About this comparison">What this site plans for {device.name} beside what it reports. Firmware, address and uptime have no planned setting to compare against and are listed above as facts.</Info>}>
      <h3>The device</h3>
      {isSwitch ? <SwitchPair read={read} status={readStatus} /> : <AccessPointPair serial={device.serial} convergence={convergence} />}
      {isSwitch && (
        <>
          <h3>The ports</h3>
          <PortsCompared plan={plan} planned={planned} selected={selected} onSelect={onSelect} />
        </>
      )}
    </Card>
  );
}

/** A number, or why there is none. */
function Gen({ value }: { value?: number }) {
  return value === undefined ? <span className="muted">not reported</span> : <code>{value}</code>;
}

/** An access point's pair with its state, from the convergence read. */
export function AccessPointPair({ serial, convergence }: { serial: string; convergence?: ReturnType<typeof useConvergence> }) {
  if (!convergence || convergence.status === "loading") return <Loading label="Loading convergence" />;
  if (convergence.status === "error") return <p className="muted">Convergence could not be read: {convergence.problem.message}</p>;
  if (convergence.status !== "ready") return <p className="muted">Convergence was not read.</p>;
  const d = convergence.data.devices.find((x) => x.serial === serial);
  if (!d) {
    const ex = convergence.data.excluded.find((x) => x.serial === serial);
    return <p className="muted">Not judged{ex ? <>: {ex.reason}</> : convergence.data.reason ? <>: {convergence.data.reason === "placement" ? "the site's placement does not resolve" : "the site has no venue"}</> : null}.</p>;
  }
  const c = convergenceWord(d);
  return (
    <dl className="kv" aria-label="Configuration generations">
      <dt>State</dt><dd><Pill tone={c.tone}>{c.label}</Pill>{d.detail && <span className="dim"> · {d.detail}</span>}</dd>
      <dt>Held</dt><dd><Gen value={d.held} /> <span className="dim">the controller&rsquo;s</span></dd>
      <dt>Running</dt><dd><Gen value={d.running} /> <span className="dim">the device&rsquo;s own report</span></dd>
    </dl>
  );
}

/** A switch's pair from its own read: two numbers and whether they differ.
 * No state word here: since ADR-0074 a switch has one, computed by the site's
 * convergence read and shown in the head and the list's Running column, and
 * this tab was left as it was (#534) rather than rendering it a second time. */
export function SwitchPair({ read, status }: { read?: Device; status: string }) {
  if (status === "loading") return <Loading label="Loading the switch" />;
  const g = read?.configuration_generations;
  if (!g) return <p className="muted">The switch&rsquo;s generations were not read{status === "error" ? ": the device read failed" : ""}.</p>;
  return (
    <dl className="kv" aria-label="Configuration generations">
      {/* Restates ADR-0074 decision 1, ADR-0078 — the two numbers are held and running
          and the words say which is which; a switch is judged against the generation
          its own controller holds. */}
      <dt>Held</dt><dd><Gen value={g.held} /> <span className="dim">the controller&rsquo;s</span></dd>
      <dt>Running</dt><dd><Gen value={g.running} /> <span className="dim">the switch&rsquo;s own report</span></dd>
      {/* Restates ADR-0078 — agreement between two generations is not agreement between
          two configurations: a change made at the switch's console moves no generation,
          so this may never be rendered as the documents matching. */}
      <dt>Compared</dt><dd>
        {g.differs
          ? <Pill tone="warn" title="The switch reports running a different generation from the one the controller holds.">differs</Pill>
          : g.held !== undefined && g.running !== undefined
            ? <span title={"Two generation numbers agree. That is not the switch’s configuration agreeing with the controller’s copy: a change made at the switch by hand moves no generation."}>the switch reports the generation its controller holds</span>
            : <span className="muted">not both reported</span>}
        {g.detail && <span className="dim"> · {g.detail}</span>}
      </dd>
    </dl>
  );
}

/** One field of one port: what the cell holds for it, and where the plan marks
 * the field, what the apply would make it. */
function Cell({ change, field }: { change: Change; field: (typeof ROWS)[number]["field"] }) {
  const held = word(field, change.held) ?? "no key";
  if (!change.differs.includes(field)) return <td>{held === "no key" ? <span className="muted">no key</span> : held}</td>;
  const planned = word(field, change.planned) ?? "no key";
  return (
    <td className="is-diff" title="Applying the plan would change this field; the server compared the two configurations.">
      <span>{held}</span> &rarr; <strong>{planned}</strong>
    </td>
  );
}

/** Every port at once, from the plan on screen. */
export function PortsCompared({ plan, planned = false, selected, onSelect }: { plan?: Plan; planned?: boolean; selected?: string; onSelect?(port: string): void }) {
  if (!plan) {
    return planned
      ? <p className="hint">This comparison is filled by a plan, which reads what the controller holds for this switch. Review &amp; apply makes a plan, which compares every port at once.</p>
      : <p className="hint">This comparison is filled by a plan, which reads what the controller holds for this switch, and a plan is made on Design › Wired&rsquo;s Ports tab, beside the editor. This page makes no plan.</p>;
  }
  if (!plan.changes) return <p className="muted">This server&rsquo;s plan carries no comparison.</p>;
  const changing = plan.changes.filter((c) => c.differs.length > 0).length;
  return (
    <>
      <p className="small">
        {plan.changes.length} {plural(plan.changes.length, "port")} in the switch&rsquo;s configuration;{" "}
        {changing === 0 ? "applying the plan changes none of the fields this build models" : <>applying the plan changes {changing}</>}.
        {/* What the table does not compare, behind the ⓘ beside its count (#1900). */}
        <Info label="About what is compared">Only the fields this build models are compared, and the marks are the plan&rsquo;s own. A section this build does not model is not listed, and its absence here is not &ldquo;nothing else changes&rdquo;: the whole configuration is the plan&rsquo;s request.</Info>
      </p>
      <div className="table-wrap"><table className="compare ports" aria-label="Every port, held beside the plan">
        <thead><tr><th>Port</th>{ROWS.map((r) => <th key={r.field}>{r.label}</th>)}<th>Compared</th></tr></thead>
        <tbody>{plan.changes.map((c) => (
          <tr key={c.port} className={selected === c.port ? "is-selected" : undefined} aria-selected={selected === c.port}>
            <td>{onSelect
              ? <button type="button" className="btn btn-quiet port-name" aria-label={`Open ${c.port}`} onClick={() => onSelect(c.port)}><PortName name={c.port} /></button>
              : <PortName name={c.port} />}</td>
            {ROWS.map((r) => <Cell key={r.field} change={c} field={r.field} />)}
            <td>{!c.planned
              ? <span className="muted">no planned settings; sent as the controller holds it</span>
              : c.differs.length === 0
                ? <span className="muted">rewritten to what it says</span>
                : <Pill tone="warn">{c.differs.length} {plural(c.differs.length, "field")} change</Pill>}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </>
  );
}
